import { EventEmitter, Injectable } from "@angular/core";
import { NmsWizardStepMapData } from "@nms-angular-toolkit/nms-wizard";
import { DwdmCircuitViewData } from "../../dwdm-circuit.model";
import { EndsStepModel } from "../steps/1-ends/ends-step.model";
import { TransponderPort, TranspondersStepModel } from "../steps/2-transponders/transponders-step.model";
import { IntermediatesStepModel } from "../steps/3-intermediates/intermediates-step.model";
import { Amplifier, AmplifiersStepModel } from "../steps/5-amplifiers/amplifiers-step.model";
import { LinkInfoCircuit, LinksStepModel } from "../steps/4-links/links-step.model";
import { OscManagementStepModel, OscPort } from "../steps/6-osc-management/osc-management-step.model";
import { GeneralStepModel } from "../steps/7-general/general-step.model";
import { LinkFormatterService } from "../steps/4-links/services/link-formatter.service";
import { CircuitDiagram } from "../../diagram/models/diagram.models";

/**
 * Serviço responsável por preencher os dados das wizards em uma edição de circuitos DWDM.
 * Foi necessário setar cada variável do step, caso contrário ao ir para pŕoxima página ocorria
 * o erro de getClassName ocasionado no angular-toolkit e o sistema não ia para a próxima página.
 *
 * Serviço utilitário responsável por emitir eventos relacionados ao estado e atualização do diagrama no wizard.
 *
 * Esses eventos permitem comunicação desacoplada entre componentes,
 * evitando dependências diretas.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmCircuitWizardUtilsService {
    updateDiagram: EventEmitter<CircuitDiagram> = new EventEmitter();
    hideDiagram: EventEmitter<boolean> = new EventEmitter();

    constructor(private readonly linkFormatterService: LinkFormatterService) { }

    /**
     * Calcula o hash de um objeto.
     *
     * @param data Objeto a ser convertido em string para a extração do hash.
     * @returns number resultante do cálculo do hash.
     */
    calculateHash(data: Object): number {
        if (!data) {
            return 0;
        }

        const rawString = typeof data === "string" ? data : JSON.stringify(data);
        let hash = 0;
        if (rawString.length === 0) {
            return hash;
        }

        for (let i = 0; i < rawString.length; i++) {
            const char = rawString.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash |= 0;
        }
        return hash;
    }

    /**
     * Calcula o hash do cache do circuito.
     * @param wizardStepsCacheMap Cache do circuito para ser sanitizado.
     * @returns number resultante do cálculo do hash.
     */
    calculateCacheHash(wizardStepsCacheMap: NmsWizardStepMapData): number {
        const jsonString = this.prepareCache(wizardStepsCacheMap);

        return this.calculateHash(jsonString);
    }

    buildOrderedLink(link: LinkInfoCircuit): LinkInfoCircuit {
        delete link?.aEndPortModel;
        delete link?.zEndPortModel;
        delete link.attenuation;
        delete link.distance;
        return this.reorderObjectProperties(link) as LinkInfoCircuit;
    }

    /**
     * Preenche os dados de edição do circuito DWDM
     * @param dwdmCircuitViewdata - dados do circuito
     * @returns - mapa com os dados das wizards.
     */
    async fillWizardSteps(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number): Promise<NmsWizardStepMapData> {
        const wizardStepsCacheMap: NmsWizardStepMapData = {};
        const [stepModel, transpondersStepModel, intermediatesStepModel, amplifiersStepModel, linksStepModel, oscStepModel,
            generalStepModel] = await Promise.all([
                this.buildEndsStepModel(dwdmCircuitViewdata, circuitId),
                this.buildTranspondersStepModel(dwdmCircuitViewdata, circuitId),
                this.buildIntermediateStepModel(dwdmCircuitViewdata),
                this.buildAmplifiersStepModel(dwdmCircuitViewdata, circuitId),
                this.buildLinksStepModel(dwdmCircuitViewdata),
                this.buildOscStepModel(dwdmCircuitViewdata, circuitId),
                this.buildGeneralStepModel(dwdmCircuitViewdata, circuitId)
            ]);

        wizardStepsCacheMap["EndsStepModel"] = stepModel;
        wizardStepsCacheMap["TranspondersStepModel"] = transpondersStepModel;
        wizardStepsCacheMap["IntermediatesStepModel"] = intermediatesStepModel;
        wizardStepsCacheMap["LinksStepModel"] = linksStepModel;
        wizardStepsCacheMap["AmplifiersStepModel"] = amplifiersStepModel;
        wizardStepsCacheMap["OscManagementStepModel"] = oscStepModel;
        wizardStepsCacheMap["GeneralStepModel"] = generalStepModel;

        return wizardStepsCacheMap;
    }

    /**
     * Realiza a "limpeza" dos dados trafegados no cache para que se torne equivalente aos dados recebidos do back.
     *
     * A remoção de algumas propriedades se faz necessárias pois são dados que são inseridos durante a navegação
     * entre os passos e que não são persistidos. Sendo eles:
     *
     *    Chave               |  Valores possíveis
     * toggleActive           |  true/false; - Dado utilizado e injetado internamente no componente do simple-table
     * identifier             |  Link <A-Z> / OSC <A-Z>, Amp <A-Z>; - Identificadores usados nas listagens.
     *                           (Links, OSC e Amplificadores)
     * profileLabel           |  Booster/Pre-Amp; Texto descritivo do perfil
     * modeLabel              |  Gain/Power; Texto descritivo do Modo;
     * hasTxPowerChanges      |  true/false; - Flag auxiliar utilizada apenas para controle interno
     *                           do wizard, não sendo persistida nem considerada
     *                           como dado relevante do circuito.
     *
     * É removido do cache para fins de comparação a propriedade "allOscs" que é utilizada no passo de Gerência OSC para que se
     * tenha a informação de todas as Oscs disponíveis e otimizar as operações na tela.
     *
     * Os passos que possuem lista de itens AmplifiersStepModel, LinksStepModel e OscManagementStepModel tem sua lista ordenada.
     *
     * O passo AmplifiersStepModel tem seu objeto recriado ainda que o usuário não interaja com o formulário, tirando as
     * propriedades da ordem original. Por esse motivo as propriedades são ordenadas.
     *
     * A informação de losAction dos amplificadores chega do back como booleano "true"/"false",
     * no fluxo das wizardas ele passa para "enabled"/"disabled". Essa informação também é transformada.
     *
     * Obs: Todas as remoções são executadas apenas para o cálculo do hash,
     * não implicando em mudanças na instância principal do cache.
     *
     * @param rawCache
     * @returns cache em formato de string sanitizado.
     */
    private prepareCache(rawCache: Object): string {
        const clonedCache = JSON.parse(JSON.stringify(rawCache));

        if (clonedCache["TranspondersStepModel"]) {
            clonedCache["TranspondersStepModel"].aEndPorts =
                clonedCache["TranspondersStepModel"].aEndPorts.sort((a, b) => this.sortTransponderPorts(a, b))

            clonedCache["TranspondersStepModel"].zEndPorts =
                clonedCache["TranspondersStepModel"].zEndPorts.sort((a, b) => this.sortTransponderPorts(a, b))
        }

        if (clonedCache["AmplifiersStepModel"]?.configuredAmplifiers.length) {
            clonedCache["AmplifiersStepModel"].configuredAmplifiers =
                clonedCache["AmplifiersStepModel"].configuredAmplifiers.map(amp => this.reorderObjectProperties(amp))
        }

        if (clonedCache["LinksStepModel"]?.links.length) {
            clonedCache["LinksStepModel"].links = clonedCache["LinksStepModel"].links.map(link => this.buildOrderedLink(link))
        }

        if(clonedCache["OscManagementStepModel"]) {
            clonedCache["OscManagementStepModel"].allOscs = [];
            clonedCache["OscManagementStepModel"].oscManagementPorts =
                clonedCache["OscManagementStepModel"].oscManagementPorts.sort((a, b) => this.sortOscPorts(a, b));
        }

        const auxiliarKeys = ["toggleActive", "identifier", "profileLabel", "modeLabel", "hasTxPowerChanges"];
        const auxiliarValues = [
            "true",
            "false",
            "\"Link [a-zA-Z]+\"",
            "\"OSC [a-zA-Z]+\"",
            "\"Amp [a-zA-Z]+\"",
            "\"Booster\"",
            "\"Pre-Amp\"",
            "\"Gain\"",
            "\"Power\""
        ];
        const commonPattern = String.raw`"(${auxiliarKeys.join("|")})":\s*(?:${auxiliarValues.join("|")})`;
        const afterComma = new RegExp(String.raw`,\s*${commonPattern}`, "g");
        const beforeComma = new RegExp(String.raw`\s*${commonPattern},?`, "g");
        const rawString = JSON.stringify(clonedCache);
        return rawString
            .replace(afterComma, "")
            .replace(beforeComma, "")
            .replace(/"Enabled"/g, "true")
            .replace(/"Disabled"/g, "false");
    }

    private async buildEndsStepModel(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number): Promise<EndsStepModel> {
        const { endsStepModel } = dwdmCircuitViewdata;
        const stepModel = new EndsStepModel();
        stepModel.circuitId = circuitId;
        stepModel.aEndDevice = endsStepModel.aEndDevice;
        stepModel.aEndSlots = endsStepModel.aEndSlots;
        stepModel.aEndPorts = endsStepModel.aEndPorts;
        stepModel.aEndHasMux = endsStepModel.aEndHasMux;
        stepModel.aEndMux = endsStepModel.aEndMux;

        stepModel.zEndDevice = endsStepModel.zEndDevice;
        stepModel.zEndSlots = endsStepModel.zEndSlots;
        stepModel.zEndPorts = endsStepModel.zEndPorts;
        stepModel.zEndHasMux = endsStepModel.zEndHasMux;
        stepModel.zEndMux = endsStepModel.zEndMux;

        return stepModel;
    }

    private async buildTranspondersStepModel(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number)
        : Promise<TranspondersStepModel> {
        const { transpondersStepModel } = dwdmCircuitViewdata;
        const stepModel = new TranspondersStepModel();
        stepModel.circuitId = circuitId;
        stepModel.aEndDevice = transpondersStepModel.aEndDevice;
        stepModel.zEndDevice = transpondersStepModel.zEndDevice;
        stepModel.aEndPorts = transpondersStepModel.aEndPorts;
        stepModel.zEndPorts = transpondersStepModel.zEndPorts;

        return stepModel;
    }

    private async buildIntermediateStepModel(dwdmCircuitViewdata: DwdmCircuitViewData): Promise<IntermediatesStepModel> {
        const { intermediatesStepModel } = dwdmCircuitViewdata;
        const stepModel = new IntermediatesStepModel();
        stepModel.devices = intermediatesStepModel.devices || [];
        stepModel.useIntermediateDevices = intermediatesStepModel.useIntermediateDevices || false;

        return stepModel;
    }

    private async buildLinksStepModel(dwdmCircuitViewdata: DwdmCircuitViewData): Promise<LinksStepModel> {
        const { linksStepModel } = dwdmCircuitViewdata;
        const stepModel = new LinksStepModel();
        stepModel.aEndResourceId = linksStepModel.aEndResourceId;
        stepModel.aEndUseMux = linksStepModel.aEndUseMux;
        stepModel.aEndMuxResourceId = linksStepModel.aEndMuxResourceId;
        stepModel.zEndResourceId = linksStepModel.zEndResourceId;
        stepModel.zEndUseMux = linksStepModel.zEndUseMux;
        stepModel.zEndMuxResourceId = linksStepModel.zEndMuxResourceId;
        stepModel.intermediatesResourceIds = linksStepModel.intermediatesResourceIds;
        stepModel.links = linksStepModel.links;

        this.linkFormatterService.formatLinks(stepModel.links);

        return stepModel;
    }

    private async buildAmplifiersStepModel(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number)
        : Promise<AmplifiersStepModel> {
        const { amplifiersStepModel } = dwdmCircuitViewdata;
        const stepModel = new AmplifiersStepModel();
        stepModel.circuitId = circuitId;
        const amplifiers = [];
        amplifiersStepModel.configuredAmplifiers.forEach((amplifier) => {
            const amplifierData = {
                portIdentifier: amplifier.portIdentifier,
                hostname: amplifier.hostname,
                links: amplifier.links,
                portName: amplifier.portName,
                portDescription: amplifier.portDescription,
                profile: amplifier.profile,
                mode: amplifier.mode,
                value: amplifier.value,
                limit: amplifier.limit,
                losAction: amplifier.losAction,
                losThreshold: amplifier.losThreshold
            } as Amplifier;
            amplifiers.push(amplifierData);
        });
        stepModel.configuredAmplifiers = amplifiers;

        return stepModel;
    }

    private async buildOscStepModel(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number)
            : Promise<OscManagementStepModel> {
        const { oscManagementPorts, useOscManagement } = dwdmCircuitViewdata.oscManagementStepModel;
        const stepModel = new OscManagementStepModel();
        stepModel.circuitId = circuitId;
        stepModel.oscManagementPorts = oscManagementPorts.map((osc) => {
            return { ...osc, toggleActive: true, oscPort: String(osc.oscPortNo) };
        });
        stepModel.useOscManagement = useOscManagement;

        return stepModel;
    }

    private async buildGeneralStepModel(dwdmCircuitViewdata: DwdmCircuitViewData, circuitId: number) {
        const { generalStepModel } = dwdmCircuitViewdata;
        const generalModel = new GeneralStepModel();
        generalModel.circuitId = circuitId;
        generalModel.name = generalStepModel.name;
        generalModel.description = generalStepModel.description;
        generalModel.generatedName = generalStepModel.generatedName;

        return generalModel;
    }

    private sortTransponderPorts(a: TransponderPort, b: TransponderPort): number {
        return a.portName.localeCompare(b.portName);
    }

    private sortOscPorts(a: OscPort, b: OscPort): number {
        return this.generateOscPortComparator(a).localeCompare(this.generateOscPortComparator(b));
    }

    /**
     * Cria uma string para identificação da porta.
     * @param port
     * @returns identificador local (Ex.: 172.25.144.142121)
     */
    private generateOscPortComparator(port: OscPort): string {
        return `${port?.endName}${port?.oscChassisIdentifier}${port?.oscSlotIdentifier}${port?.oscPort}`;
    }

    /**
     * Ordena as propriedades dos objetos para fins de comparação.
     * @param object
     * @returns objeto com as propriedades ordenadas.
     */
    private reorderObjectProperties(object: Object) {
        const sortedKeys = Object.keys(object).sort((a, b) => a.localeCompare(b));
        const sortedLink = {};
        sortedKeys.forEach(key => {
            sortedLink[key] = object[key];
        });

        return sortedLink;
    }
}
